import { DataFrame } from '@lib/mobx/dataframe';
import { assert } from '@lib/utils/assert';
import { toError } from '@lib/utils/toError';
import * as Sentry from '@sentry/react';
import {
  comparer,
  computed,
  IReactionDisposer,
  makeObservable,
  onBecomeObserved,
  onBecomeUnobserved,
  reaction,
} from 'mobx';
import { billingApi, billingStore } from './Billing';

// Mirrors atlas's `CreditUsageDocument.type` (external.api.atlas, src/clients/mongo/types.ts). The
// route's swagger block lists only the first five; the document type is the source of truth.
const CREDIT_USAGE_TYPES = ['media', 'chat', 'search', 'storage', 'render', 'external'] as const;
export type CreditUsageType = (typeof CREDIT_USAGE_TYPES)[number];

/** One category's spend over the requested window, as `GET /billing/credits/usage/stats` sums it. */
export type CreditUsageStat = {
  type: CreditUsageType;
  credits: number;
};

/** What a stats request was asked: stamped before it went out, compared before the answer shows. */
type CreditUsageStatsRequest = { since: number; creditsLeft: number | null };

export type CreditUsageStats = { request: CreditUsageStatsRequest; stats: CreditUsageStat[] };

type CreditUsageStatsResponse = { type: string; credits: number }[];

const isCreditUsageType = (value: string): value is CreditUsageType =>
  CREDIT_USAGE_TYPES.some((type) => type === value);

/** The request the stats should answer now; null without an unexpired note to open the window. */
function currentRequest(): CreditUsageStatsRequest | null {
  const since = billingStore.usageWindowStart;
  return since === null ? null : { since, creditsLeft: billingStore.creditsLeft };
}

/**
 * Credits spent per category since `billingStore.usageWindowStart`, the window whose notes the
 * credits bar counts.
 *
 * FIXME(credits-usage-gap): this ledger total exceeds the bar's "used" by whatever was spent in
 * the window from a note issued before it opened. Atlas deducts from the soonest-expiring note, so
 * once last period's subscription note expires (~15 days into each period) the window moves past
 * it while its spend stays in range: every subscriber sees the breakdown run ahead of the bar by
 * about half a month's spend. Acceptable for now: the page is display-only and both figures
 * state what they count, and atlas exposes no per-note or per-period used figure (usage documents
 * carry no note id). A backend-owned used figure for the window retires this.
 *
 * Fetched only while observed, and again whenever the balance or the window moves. Read
 * `currentStats`, which is null until the answer matches the current request.
 */
export class CreditUsageStatsStore extends DataFrame<CreditUsageStats> {
  private disposeRefetch: IReactionDisposer | null = null;

  constructor() {
    super();
    makeObservable(this);
    onBecomeObserved(this, 'data', () => {
      this.disposeRefetch = reaction(
        // Not `billingStore.data`: restoring the balance from localStorage on every window focus
        // replaces that object without changing anything this answer depends on.
        currentRequest,
        (request) => {
          if (request === null) return;
          this.get(true).catch((e: unknown) => Sentry.captureException(toError(e)));
        },
        { fireImmediately: true, equals: comparer.structural },
      );
    });
    onBecomeUnobserved(this, 'data', () => {
      this.disposeRefetch?.();
      this.disposeRefetch = null;
    });
  }

  /** Per-category spend, only once it answers the current window and balance. */
  @computed
  get currentStats(): CreditUsageStat[] | null {
    const request = currentRequest();
    if (!this.data || !request || !comparer.structural(this.data.request, request)) return null;
    return this.data.stats;
  }

  protected async fetch(signal: AbortSignal): Promise<CreditUsageStats> {
    const request = currentRequest();
    assert(request, 'credit usage stats fetched without an unexpired credit note');
    // FIXME(credits-usage-gap): see the class comment.
    const response = await billingApi<CreditUsageStatsResponse>(
      `/credits/usage/stats?termStart=${request.since}&termEnd=${Date.now()}`,
      { signal },
    );
    const stats: CreditUsageStat[] = [];
    for (const { type, credits } of response) {
      if (isCreditUsageType(type)) {
        stats.push({ type, credits });
      } else {
        // A type atlas added after this list: the known rows still render, the operator hears.
        Sentry.captureException(new Error(`unknown credit usage type: ${type}`));
      }
    }
    return { request, stats };
  }
}

export const creditUsageStatsStore = new CreditUsageStatsStore();
